import { useDebounceFn } from '@vueuse/core'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { useOrdersStore } from './store'
import { useOrderOptions } from './order-options'

const INPUT_DEBOUNCE_MS = 400
const LESSONS_BOUGHT_MIN = 0
const LESSONS_BOUGHT_MAX = 144

interface FiltersOrdersReturn {
  refSearch: WritableComputedRef<string>
  refDateRange: WritableComputedRef<Date[] | null>
  refStatuses: WritableComputedRef<string[]>
  refType: WritableComputedRef<string | undefined>
  refLessonsFrom: WritableComputedRef<string | number>
  refLessonsTo: WritableComputedRef<string | number>
  statusOptions: ComputedRef<AppSelectOption[]>
  typeOptions: ComputedRef<AppSelectOption[]>
  lessonsMin: number
  lessonsMax: number
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
  submitSearch: () => Promise<void>
}

const toQueryCount = (value: number | null): string | undefined =>
  value !== null ? String(value) : undefined

const toCount = (value: string | number): number | null => {
  const parsed = Number(value)

  return value === '' || !Number.isFinite(parsed) ? null : parsed
}

export const useFiltersOrders = (): FiltersOrdersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useOrdersStore()
  const dayjs = useDayjs()
  const { statusOptions, typeOptions } = useOrderOptions()

  /**
   * The one place a filter change reaches the URL: write the store, push the query (the page refetches on query change).
   * `page` is dropped so a new filter never lands on a page that no longer exists.
   */
  const syncQuery = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        q: store.getFilterSearch || undefined,
        status: store.getFilterStatuses.length ? store.getFilterStatuses : undefined,
        type: store.getFilterType || undefined,
        from: store.getFilterFrom || undefined,
        to: store.getFilterTo || undefined,
        lessons_from: toQueryCount(store.getFilterLessonsFrom),
        lessons_to: toQueryCount(store.getFilterLessonsTo),
        promocode_id: store.getFilterPromocodeId ? String(store.getFilterPromocodeId) : undefined,
        page: undefined,
      },
    })
  }

  const applyTyped = useDebounceFn(() => syncQuery(), INPUT_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => {
      store.setFilterSearch(value.trim() || null)
      void applyTyped()
    },
  })

  const refDateRange = computed<Date[] | null>({
    get: () => {
      const from = store.getFilterFrom
      const to = store.getFilterTo
      if (!from || !to) return null

      const fromDate = dayjs(from)
      const toDate = dayjs(to)

      return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
    },
    set: (value) => {
      if (Array.isArray(value) && value.length === 2) {
        store.setFilterFrom(dayjs(value[0]).format(DateFormat.Default))
        store.setFilterTo(dayjs(value[1]).format(DateFormat.Default))
      } else {
        store.setFilterFrom(null)
        store.setFilterTo(null)
      }

      void syncQuery()
    },
  })

  const refStatuses = computed<string[]>({
    get: () => store.getFilterStatuses,
    set: (value) => {
      store.setFilterStatuses(value)
      void syncQuery()
    },
  })

  const refType = computed<string | undefined>({
    get: () => store.getFilterType ?? undefined,
    set: (value) => {
      store.setFilterType(value ?? null)
      void syncQuery()
    },
  })

  const refLessonsFrom = computed<string | number>({
    get: () => store.getFilterLessonsFrom ?? '',
    set: (value) => {
      store.setFilterLessonsFrom(toCount(value))
      void applyTyped()
    },
  })

  const refLessonsTo = computed<string | number>({
    get: () => store.getFilterLessonsTo ?? '',
    set: (value) => {
      store.setFilterLessonsTo(toCount(value))
      void applyTyped()
    },
  })

  const isLoading = computed<boolean>(() => store.isLoading)

  const clearFilters = (): void => {
    store.resetFilters()
    void syncQuery()
  }

  /** Applies the filters at once instead of waiting out the typing debounce. */
  const submitSearch = (): Promise<void> => syncQuery()

  return {
    refSearch,
    refDateRange,
    refStatuses,
    refType,
    refLessonsFrom,
    refLessonsTo,
    statusOptions,
    typeOptions,
    lessonsMin: LESSONS_BOUGHT_MIN,
    lessonsMax: LESSONS_BOUGHT_MAX,
    isLoading,
    clearFilters,
    submitSearch,
  }
}
